<template>
  <b-card no-body>
    <b-card-header class="d-flex align-items-center justify-content-between">
      <div>
        <h4 class="mb-0">
          Lager Auslastungs-Heatmap
        </h4>
        <small class="text-muted">Auslastung nach Standorten und Zeiträumen</small>
      </div>
      <div class="d-flex gap-2">
        <b-dropdown
          text="Zeitraum"
          variant="outline-primary"
          size="sm"
          no-caret
        >
          <b-dropdown-item
            v-for="period in timePeriods"
            :key="period.value"
            :active="selectedPeriod === period.value"
            @click="updatePeriod(period.value)"
          >
            {{ period.label }}
          </b-dropdown-item>
        </b-dropdown>
        <b-dropdown
          text="Ansicht"
          variant="outline-secondary"
          size="sm"
          no-caret
        >
          <b-dropdown-item
            v-for="view in viewTypes"
            :key="view.value"
            :active="selectedView === view.value"
            @click="updateView(view.value)"
          >
            {{ view.label }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </b-card-header>
    
    <b-card-body class="px-3 py-3">
      <!-- Legend -->
      <div class="heatmap-legend mb-3">
        <div class="d-flex align-items-center justify-content-between">
          <span class="legend-label">Auslastung:</span>
          <div class="legend-scale d-flex align-items-center">
            <span class="legend-min">0%</span>
            <div class="legend-gradient mx-2" />
            <span class="legend-max">100%</span>
          </div>
        </div>
      </div>

      <!-- Heat Map Grid -->
      <div
        v-if="heatmapData.length > 0"
        class="heatmap-container"
      >
        <!-- Month/Period Headers -->
        <div class="heatmap-grid">
          <div class="heatmap-header">
            <div class="location-label">
              Standort
            </div>
            <div 
              v-for="period in periodLabels"
              :key="period"
              class="period-header"
            >
              {{ period }}
            </div>
          </div>
          
          <!-- Location Rows -->
          <div 
            v-for="locationData in heatmapData"
            :key="locationData.locationName"
            class="heatmap-row"
          >
            <div class="location-label">
              {{ locationData.locationName }}
              <small class="text-muted d-block">
                {{ locationData.totalStorages }} Lager
              </small>
            </div>
            <div 
              v-for="(utilizationData, index) in locationData.utilizationByPeriod"
              :key="index"
              v-b-tooltip.hover
              class="heatmap-cell"
              :class="getUtilizationClass(utilizationData.percentage)"
              :style="getUtilizationStyle(utilizationData.percentage)"
              :title="getCellTooltip(locationData.locationName, periodLabels[index], utilizationData)"
              @click="showCellDetails(locationData.locationName, periodLabels[index], utilizationData)"
            >
              <span class="cell-percentage">{{ Math.round(utilizationData.percentage) }}%</span>
              <small class="cell-details">
                {{ utilizationData.occupied }}/{{ utilizationData.total }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center py-5"
      >
        <feather-icon
          icon="InboxIcon"
          size="48"
          class="text-muted mb-2"
        />
        <p class="text-muted">
          Keine Daten für den ausgewählten Zeitraum verfügbar
        </p>
      </div>

      <!-- Summary Statistics -->
      <div
        v-if="summaryStats"
        class="heatmap-summary mt-4"
      >
        <b-row>
          <b-col
            cols="3"
            class="text-center"
          >
            <h5 class="text-primary mb-0">
              {{ summaryStats.averageUtilization }}%
            </h5>
            <small class="text-muted">Ø Gesamtauslastung</small>
          </b-col>
          <b-col
            cols="3"
            class="text-center"
          >
            <h5 class="text-success mb-0">
              {{ summaryStats.bestPerformingLocation }}
            </h5>
            <small class="text-muted">Bester Standort</small>
          </b-col>
          <b-col
            cols="3"
            class="text-center"
          >
            <h5 class="text-warning mb-0">
              {{ summaryStats.totalStorages }}
            </h5>
            <small class="text-muted">Gesamt Lager</small>
          </b-col>
          <b-col
            cols="3"
            class="text-center"
          >
            <h5 class="text-info mb-0">
              {{ summaryStats.activeRentals }}
            </h5>
            <small class="text-muted">Aktive Mieten</small>
          </b-col>
        </b-row>
      </div>
    </b-card-body>

    <!-- Cell Details Modal -->
    <b-modal 
      v-model="showDetailsModal" 
      :title="`Auslastungsdetails: ${selectedCellData.location}`"
      size="md"
      hide-footer
    >
      <div v-if="selectedCellData.data">
        <h6>{{ selectedCellData.period }}</h6>
        <hr>
        
        <div class="detail-metrics">
          <div class="metric-row d-flex justify-content-between">
            <span class="text-muted">Auslastung:</span>
            <strong>{{ Math.round(selectedCellData.data.percentage) }}%</strong>
          </div>
          <div class="metric-row d-flex justify-content-between">
            <span class="text-muted">Vermietete Lager:</span>
            <strong>{{ selectedCellData.data.occupied }}</strong>
          </div>
          <div class="metric-row d-flex justify-content-between">
            <span class="text-muted">Verfügbare Lager:</span>
            <strong>{{ selectedCellData.data.available }}</strong>
          </div>
          <div class="metric-row d-flex justify-content-between">
            <span class="text-muted">Gesamt Lager:</span>
            <strong>{{ selectedCellData.data.total }}</strong>
          </div>
          <div class="metric-row d-flex justify-content-between">
            <span class="text-muted">Umsatz (geschätzt):</span>
            <strong>{{ formatCurrency(selectedCellData.data.estimatedRevenue || 0) }}</strong>
          </div>
        </div>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BRow, BCol, BDropdown, BDropdownItem, BModal } from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import moment from 'moment'
import businessMetrics from '@/helper/businessMetrics'

export default {
  name: 'StorageHeatMap',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BDropdown,
    BDropdownItem,
    BModal,
    FeatherIcon,
  },
  setup() {
    const dashboardMetrics = businessMetrics()
    return {
      ...dashboardMetrics,
    }
  },
  data() {
    return {
      selectedPeriod: 'last6months',
      selectedView: 'monthly',
      timePeriods: [
        { value: 'last3months', label: 'Letzte 3 Monate' },
        { value: 'last6months', label: 'Letzte 6 Monate' },
        { value: 'last12months', label: 'Letztes Jahr' },
        { value: 'last24months', label: 'Letzte 2 Jahre' },
      ],
      viewTypes: [
        { value: 'monthly', label: 'Monatlich' },
        { value: 'quarterly', label: 'Quartalsweise' },
        { value: 'yearly', label: 'Jährlich' },
      ],
      heatmapData: [],
      periodLabels: [],
      summaryStats: null,
      showDetailsModal: false,
      selectedCellData: {
        location: '',
        period: '',
        data: null
      }
    }
  },
  mounted() {
    this.loadHeatmapData()
  },
  methods: {
    loadHeatmapData() {
      try {
        if (!this.$store || !this.$store.getters) {
          console.warn('Store not available')
          this.heatmapData = []
          return
        }
        
        const locations = this.$store.getters.locationsEntries || []
        const storages = this.$store.getters.storageEntries || []
        
        console.log('Locations found:', locations.length)
        console.log('Storages found:', storages.length)
        
        // Compare with StorageDashboard calculation method
        locations.forEach(location => {
          const locationStorages = storages.filter(storage => storage.location_id === location.id || (storage.location && storage.location.id === location.id))
          console.log(`\n=== COMPARISON FOR ${location.name} ===`)
          console.log(`Total storages: ${locationStorages.length}`)
          
          // Method 1: Current working method from StorageDashboard
          const rentedCount = locationStorages.filter(storage => storage.order_items && storage.order_items.some(item => !item.moveOutDate)).length
          const utilization1 = locationStorages.length > 0 ? Math.round((rentedCount / locationStorages.length) * 100) : 0
          console.log(`Method 1 (StorageDashboard): ${rentedCount}/${locationStorages.length} = ${utilization1}%`)
          
          // Method 2: Current period-based method from heatmap  
          const currentPeriodData = this.calculateUtilizationForCurrentPeriod(locationStorages)
          console.log(`Method 2 (Heatmap current): ${currentPeriodData.occupied}/${currentPeriodData.total} = ${Math.round(currentPeriodData.percentage)}%`)
          
          console.log('Detailed storage analysis:')
          locationStorages.forEach((storage, i) => {
            const hasOrderItems = storage.order_items && storage.order_items.length > 0
            const activeRental = hasOrderItems ? storage.order_items.some(item => !item.moveOutDate) : false
            console.log(`  Storage ${i + 1} (ID: ${storage.id}): has_order_items=${hasOrderItems}, active_rental=${activeRental}`)
            if (hasOrderItems) {
              storage.order_items.forEach((item, j) => {
                console.log(`    OrderItem ${j + 1}: rentStart=${item.rentStart}, moveOutDate=${item.moveOutDate}`)
              })
            }
          })
        })
        
        if (locations.length === 0) {
          console.warn('No locations found - data may still be loading')
          this.heatmapData = []
          return
        }

        // Generate period labels based on selected period and view
        this.generatePeriodLabels()
        
        console.log('Period labels generated:', this.periodLabels)
        
        // Calculate utilization data for each location and period
        this.heatmapData = locations.map(location => {
          const locationStorages = storages.filter(storage => storage.location_id === location.id || (storage.location && storage.location.id === location.id))
          const utilizationByPeriod = this.periodLabels.map(periodLabel => this.calculateUtilizationForPeriod(locationStorages, periodLabel))

          console.log(`Location ${location.name}: ${locationStorages.length} storages`)
          console.log('Location storages:', locationStorages)
          
          return {
            locationName: location.name || `Standort ${location.id}`,
            locationId: location.id,
            totalStorages: locationStorages.length,
            utilizationByPeriod
          }
        })
        
        console.log('Final heatmap data:', this.heatmapData)
        console.log('Heatmap data length:', this.heatmapData.length)

        // Calculate summary statistics
        this.calculateSummaryStats()
      } catch (error) {
        console.error('Error loading heatmap data:', error)
        this.heatmapData = []
      }
    },
    
    generatePeriodLabels() {
      const labels = []
      const months = this.getMonthsCount()
      
      if (this.selectedView === 'monthly') {
        for (let i = months - 1; i >= 0; i -= 1) {
          const date = moment().subtract(i, 'months')
          labels.push(date.format('MMM YY'))
        }
      } else if (this.selectedView === 'quarterly') {
        const quarters = Math.ceil(months / 3)
        for (let i = quarters - 1; i >= 0; i -= 1) {
          const date = moment().subtract(i * 3, 'months')
          labels.push(`Q${date.quarter()} ${date.format('YY')}`)
        }
      } else if (this.selectedView === 'yearly') {
        const years = Math.ceil(months / 12)
        for (let i = years - 1; i >= 0; i -= 1) {
          const date = moment().subtract(i, 'years')
          labels.push(date.format('YYYY'))
        }
      }
      
      this.periodLabels = labels
    },
    
    getMonthsCount() {
      switch (this.selectedPeriod) {
        case 'last3months': return 3
        case 'last6months': return 6
        case 'last12months': return 12
        case 'last24months': return 24
        default: return 6
      }
    },
    
    calculateUtilizationForCurrentPeriod(storages) {
      // Use current month for comparison
      const now = moment()
      const startDate = now.clone().startOf('month')
      const endDate = now.clone().endOf('month')
      
      let occupiedCount = 0
      storages.forEach(storage => {
        if (storage.order_items && storage.order_items.length > 0) {
          const wasOccupied = storage.order_items.some(orderItem => {
            if (!orderItem || !orderItem.rentStart) return false
            
            const rentStart = moment(orderItem.rentStart)
            const moveOut = orderItem.moveOutDate ? moment(orderItem.moveOutDate) : null
            
            // Check if rental overlaps with current month
            return rentStart.isBefore(endDate) && (!moveOut || moveOut.isAfter(startDate))
          })
          
          if (wasOccupied) {
            occupiedCount += 1
          }
        }
      })

      const percentage = storages.length > 0 ? (occupiedCount / storages.length) * 100 : 0
      return {
        percentage,
        occupied: occupiedCount,
        total: storages.length,
        available: storages.length - occupiedCount
      }
    },
    
    calculateUtilizationForPeriod(storages, periodLabel) {
      if (!storages || storages.length === 0) {
        return { percentage: 0, occupied: 0, total: 0, available: 0, estimatedRevenue: 0 }
      }
      
      try {
        // Parse period label to get date range
        const { startDate, endDate } = this.parsePeriodLabel(periodLabel)
        let occupiedCount = 0
        let estimatedRevenue = 0

        console.log(`Calculating utilization for period ${periodLabel}, date range: ${startDate.format('YYYY-MM-DD')} to ${endDate.format('YYYY-MM-DD')}`)

        storages.forEach(storage => {
          if (!storage) return
          
          console.log('Processing storage:', storage.id, 'order_items:', storage.order_items)
          
          if (storage.order_items && storage.order_items.length > 0) {
            const wasOccupied = storage.order_items.some(orderItem => {
              if (!orderItem || !orderItem.rentStart) {
                console.log('Skipping order item - no rentStart:', orderItem)
                return false
              }
              
              const rentStart = moment(orderItem.rentStart)
              const moveOut = orderItem.moveOutDate ? moment(orderItem.moveOutDate) : null
              
              console.log(`OrderItem ${orderItem.id}: rentStart=${rentStart.format('YYYY-MM-DD')}, moveOut=${moveOut ? moveOut.format('YYYY-MM-DD') : 'ongoing'}`)
              
              // Check if rental overlaps with period
              const overlaps = rentStart.isBefore(endDate) && (!moveOut || moveOut.isAfter(startDate))
              
              console.log(`Overlaps with period: ${overlaps}`)
              
              if (overlaps) {
                // Calculate revenue for this period
                const effectiveStart = moment.max(rentStart, startDate)
                const effectiveEnd = moveOut ? moment.min(moveOut, endDate) : endDate
                const monthsInPeriod = effectiveEnd.diff(effectiveStart, 'months', true)
                estimatedRevenue += Math.max(monthsInPeriod, 0) * (orderItem.price || 0)
              }
              
              return overlaps
            })
            
            if (wasOccupied) {
              occupiedCount += 1
              console.log(`Storage ${storage.id} was occupied in period`)
            }
          } else {
            console.log(`Storage ${storage.id} has no order_items`)
          }
        })

        const percentage = (occupiedCount / storages.length) * 100
        console.log(`Period ${periodLabel}: ${occupiedCount}/${storages.length} = ${percentage}%`)
        
        return {
          percentage,
          occupied: occupiedCount,
          total: storages.length,
          available: storages.length - occupiedCount,
          estimatedRevenue: Math.round(estimatedRevenue)
        }
      } catch (error) {
        console.error('Error calculating utilization for period:', error)
        return { percentage: 0, occupied: 0, total: storages.length, available: storages.length, estimatedRevenue: 0 }
      }
    },
    
    parsePeriodLabel(periodLabel) {
      let startDate
      let endDate
      
      if (this.selectedView === 'monthly') {
        // Format: "MMM YY" (e.g., "Jan 23")
        const date = moment(periodLabel, 'MMM YY')
        startDate = date.clone().startOf('month')
        endDate = date.clone().endOf('month')
      } else if (this.selectedView === 'quarterly') {
        // Format: "Q1 23"
        const [quarter, year] = periodLabel.split(' ')
        const quarterNum = parseInt(quarter.replace('Q', ''), 10)
        const fullYear = 2000 + parseInt(year, 10)
        startDate = moment().year(fullYear).quarter(quarterNum).startOf('quarter')
        endDate = moment().year(fullYear).quarter(quarterNum).endOf('quarter')
      } else if (this.selectedView === 'yearly') {
        // Format: "2023"
        const year = parseInt(periodLabel, 10)
        startDate = moment().year(year).startOf('year')
        endDate = moment().year(year).endOf('year')
      }
      
      return { startDate, endDate }
    },
    
    calculateSummaryStats() {
      if (this.heatmapData.length === 0) {
        this.summaryStats = null
        return
      }

      let totalUtilization = 0
      let totalStorages = 0
      let totalActiveRentals = 0
      let bestLocation = ''
      let bestUtilization = 0

      this.heatmapData.forEach(locationData => {
        const avgUtilization = locationData.utilizationByPeriod.reduce((sum, period) => sum + period.percentage, 0) / locationData.utilizationByPeriod.length
        
        totalUtilization += avgUtilization
        totalStorages += locationData.totalStorages
        
        // Get current period active rentals (last period in array)
        const lastPeriod = locationData.utilizationByPeriod[locationData.utilizationByPeriod.length - 1]
        totalActiveRentals += lastPeriod.occupied
        
        if (avgUtilization > bestUtilization) {
          bestUtilization = avgUtilization
          bestLocation = locationData.locationName
        }
      })

      this.summaryStats = {
        averageUtilization: Math.round(totalUtilization / this.heatmapData.length),
        bestPerformingLocation: bestLocation,
        totalStorages,
        activeRentals: totalActiveRentals
      }
    },
    
    updatePeriod(period) {
      this.selectedPeriod = period
      this.loadHeatmapData()
    },
    
    updateView(view) {
      this.selectedView = view
      this.loadHeatmapData()
    },
    
    getUtilizationClass(percentage) {
      if (percentage >= 90) return 'utilization-excellent'
      if (percentage >= 75) return 'utilization-good'
      if (percentage >= 50) return 'utilization-fair'
      if (percentage >= 25) return 'utilization-poor'
      return 'utilization-critical'
    },
    
    getUtilizationStyle(percentage) {
      // Create gradient from red (0%) to green (100%)
      const red = Math.max(0, 255 - (percentage * 2.55))
      const green = Math.min(255, percentage * 2.55)
      const blue = 0
      const opacity = Math.max(0.1, (percentage / 100) * 0.8)
      
      return {
        backgroundColor: `rgba(${red}, ${green}, ${blue}, ${opacity})`,
        color: percentage > 60 ? '#fff' : '#000'
      }
    },
    
    showCellDetails(location, period, data) {
      this.selectedCellData = {
        location,
        period,
        data
      }
      this.showDetailsModal = true
    },
    
    getCellTooltip(location, period, data) {
      return `${location} - ${period}\nAuslastung: ${Math.round(data.percentage)}%\nVermietet: ${data.occupied}/${data.total}\nUmsatz: ${this.formatCurrency(data.estimatedRevenue || 0)}`
    },
    
    formatCurrency(amount) {
      return `${amount.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`
    },
    
    generateMockData() {
      // Generate period labels first
      this.generatePeriodLabels()
      
      // Generate mock locations
      const mockLocations = [
        { id: 1, name: 'Hamburg Zentrum', totalStorages: 25 },
        { id: 2, name: 'Berlin Nord', totalStorages: 32 },
        { id: 3, name: 'München Süd', totalStorages: 18 },
        { id: 4, name: 'Frankfurt Ost', totalStorages: 28 }
      ]
      
      this.heatmapData = mockLocations.map(location => {
        const utilizationByPeriod = this.periodLabels.map(() => {
          // Generate random utilization data
          const occupied = Math.floor(Math.random() * location.totalStorages)
          const percentage = (occupied / location.totalStorages) * 100
          const estimatedRevenue = occupied * (Math.random() * 200 + 100) // Random revenue per storage
          
          return {
            percentage,
            occupied,
            total: location.totalStorages,
            available: location.totalStorages - occupied,
            estimatedRevenue: Math.round(estimatedRevenue)
          }
        })
        
        return {
          locationName: location.name,
          locationId: location.id,
          totalStorages: location.totalStorages,
          utilizationByPeriod
        }
      })
      
      console.log('Generated mock heatmap data:', this.heatmapData)
      
      // Calculate summary statistics
      this.calculateSummaryStats()
    }
  }
}
</script>

<style scoped>
.heatmap-legend {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.legend-gradient {
  width: 200px;
  height: 20px;
  background: linear-gradient(to right, #ff4444, #ffaa00, #44ff44);
  border-radius: 10px;
  border: 1px solid #ddd;
}

.legend-min, .legend-max {
  font-size: 0.875rem;
  color: #6c757d;
}

.heatmap-container {
  overflow-x: auto;
}

.heatmap-grid {
  min-width: 600px;
}

.heatmap-header {
  display: grid;
  grid-template-columns: 200px repeat(auto-fit, minmax(100px, 1fr));
  gap: 2px;
  margin-bottom: 2px;
}

.heatmap-row {
  display: grid;
  grid-template-columns: 200px repeat(auto-fit, minmax(100px, 1fr));
  gap: 2px;
  margin-bottom: 2px;
}

.location-label {
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
}

.period-header {
  padding: 8px;
  background: #e9ecef;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-weight: 600;
  text-align: center;
  font-size: 0.875rem;
}

.heatmap-cell {
  padding: 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.heatmap-cell:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  z-index: 2;
}

.cell-percentage {
  font-weight: 600;
  font-size: 0.875rem;
}

.cell-details {
  font-size: 0.75rem;
  opacity: 0.8;
  margin-top: 2px;
}

.utilization-excellent {
  background-color: #28a745 !important;
  color: white !important;
}

.utilization-good {
  background-color: #68bb3c !important;
  color: white !important;
}

.utilization-fair {
  background-color: #ffc107 !important;
  color: black !important;
}

.utilization-poor {
  background-color: #fd7e14 !important;
  color: white !important;
}

.utilization-critical {
  background-color: #dc3545 !important;
  color: white !important;
}

.heatmap-summary {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.detail-metrics .metric-row {
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.detail-metrics .metric-row:last-child {
  border-bottom: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .heatmap-header,
  .heatmap-row {
    grid-template-columns: 150px repeat(auto-fit, minmax(80px, 1fr));
  }
  
  .location-label {
    padding: 8px;
  }
  
  .heatmap-cell {
    padding: 6px;
    min-height: 50px;
  }
  
  .cell-percentage {
    font-size: 0.75rem;
  }
  
  .cell-details {
    font-size: 0.65rem;
  }
}

@media (max-width: 576px) {
  .legend-gradient {
    width: 150px;
  }
  
  .heatmap-header,
  .heatmap-row {
    grid-template-columns: 120px repeat(auto-fit, minmax(70px, 1fr));
  }
}
</style>
